import { http } from "./client";

// Admin / settings: users, audit logs, import history, storage configs,
// scheduled syncs and bulk actions.
// Each function returns the response body (same data the old Redux thunks returned).
const data = (r) => r.data;

export const userApi = {
  getAll: () =>
    http.get("/users").then((r) =>
      r.data.map((user) => ({
        ...user,
        employeeId: user.employeeId ?? null,
        companyId: user.companyId ?? null,
      })),
    ),
  // GET /users?search&status=active|inactive&roleId&companyId&page&limit → { data, total, page, limit }
  // (scoped to the caller's company tree; companyId is left as the server sent it)
  getPage: (params = {}) =>
    http
      .get("/users", {
        params: Object.fromEntries(
          Object.entries(params).filter(
            ([, v]) => v !== undefined && v !== null && v !== "",
          ),
        ),
      })
      .then(data),
  // Creates when there's no id, updates otherwise
  save: ({ id, ...payload }) =>
    (id
      ? http.patch(`/users/${id}`, payload)
      : http.post("/users", payload)
    ).then(data),
  remove: (id) => http.delete(`/users/${id}`).then(() => id),
  // GET /users/linkable-employees?companyId&branchId&search&userId&limit
  // → [{ id, employeeNumber, name, companyEmail, companyId, branchId, branchName }]
  // Exactly that company and branch, not separated, not linked to another
  // account (userId keeps the edited account's own employee in the list)
  getLinkableEmployees: (params = {}) =>
    http
      .get("/users/linkable-employees", {
        params: Object.fromEntries(
          Object.entries(params).filter(
            ([, v]) => v !== undefined && v !== null && v !== "",
          ),
        ),
      })
      .then(data),
};

export const auditLogApi = {
  // GET /audit-logs/modules → ["Attendance", "DTR", …]
  getModules: () => http.get("/audit-logs/modules").then(data),
  /**
   * GET /audit-logs (admin/HR, scoped to their company tree) → { data, total, page, limit }
   * Rows: { id, module, entityName, entityId, action, oldValues, newValues, reason,
   *         updatedBy: { id, name }, updatedAt, employee: {…} | null }
   * filters: module, entityName, entityId, action, employeeId, updatedBy, from, to, search, page, limit
   */
  getAll: (filters = {}) =>
    http
      .get("/audit-logs", {
        params: Object.fromEntries(
          Object.entries(filters).filter(
            ([, v]) => v !== undefined && v !== null && v !== "" && v !== "All",
          ),
        ),
      })
      .then(data),
  getById: (id) => http.get(`/audit-logs/${id}`).then(data),
};

export const importLogApi = {
  /** → { history, totalCount } */
  getHistory: ({ limit = 200, skip = 0 } = {}) =>
    http.get("/import-logs", { params: { limit, skip } }).then((r) => {
      const history = r.data?.data || (Array.isArray(r.data) ? r.data : []);
      return { history, totalCount: r.data?.total || history.length || 0 };
    }),
  /** → items array of one import batch */
  getBatchDetails: (batchId) =>
    http
      .get(`/import-logs/${batchId}/details`)
      .then((r) => r.data?.items || []),
};

export const storageConfigApi = {
  getTemplate: (provider) =>
    http.get(`/storage-configs/template/${provider}`).then(data),
  getAll: () => http.get("/storage-configs").then(data),
  getForCompany: () => http.get("/storage-configs/company").then(data),
  create: (payload) => http.post("/storage-configs", payload).then(data),
  updateValue: ({ id, configValue, isActive }) =>
    http
      .patch(`/storage-configs/${id}/value`, { configValue, isActive })
      .then(data),
};

export const syncConfigApi = {
  getAll: () => http.get("/bulk-action/configs").then(data),
  create: (dto) => http.post("/bulk-action/config", dto).then(data),
  update: (id, dto) => http.put(`/bulk-action/config/${id}`, dto).then(data),
  remove: (id) => http.delete(`/bulk-action/config/${id}`).then(() => id),
  run: (id) =>
    http
      .post(`/bulk-action/config/${id}/run`)
      .then((r) => ({ id, message: r.data.message })),
};

export const bulkActionApi = {
  triggerSync: (payload) => http.post("/bulk-action/sync", payload).then(data),
};
